import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Lock, AlertTriangle, Loader2, TrendingDown, TrendingUp, ShieldAlert, Clock } from 'lucide-react';

/**
 * Publicly accessible investigation share page.
 *
 * Route: `/investigations/shared/:shareId`
 *
 * Rendered without any auth — anyone with the share link can view.
 * Password (if the sharer set one) is collected inline and sent via
 * the `X-Share-Password` header so it stays out of URL logs.
 *
 * Handles the full error taxonomy from `/api/public/investigations/:id`:
 * - 404: unknown / revoked link
 * - 410: expired
 * - 401 password_required / invalid_password
 */

const API = process.env.REACT_APP_BACKEND_URL || '';

async function fetchShare(shareId, password) {
  const res = await fetch(`${API}/api/public/investigations/${encodeURIComponent(shareId)}`, {
    headers: password ? { 'X-Share-Password': password } : {},
  });
  const body = await res.json().catch(() => ({}));
  if (res.ok) return { ok: true, data: body };
  const detail = body?.detail || {};
  return {
    ok: false,
    status: res.status,
    code: detail?.code,
    message: detail?.message || `HTTP ${res.status}`,
    hasPassword: detail?.has_password || false,
  };
}

function Hypothesis({ h, index }) {
  const delta = h.delta_pct;
  const negative = delta !== null && delta !== undefined && delta < 0;
  const Icon = negative ? TrendingDown : TrendingUp;
  const confPct = h.confidence != null ? Math.round(h.confidence * 100) : null;
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800/60 p-3 space-y-1.5"
         data-testid={`public-investigation-hypothesis-${index}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-slate-400">{h.dimension}</div>
          <div className="text-sm font-semibold truncate text-slate-100">{h.value}</div>
        </div>
        {delta != null && (
          <div className={`flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md ${
            negative ? 'bg-rose-500/20 text-rose-300' : 'bg-emerald-500/20 text-emerald-300'
          }`}>
            <Icon className="w-3 h-3" />
            {delta > 0 ? '+' : ''}{delta.toFixed(1)}%
          </div>
        )}
      </div>
      {h.explanation && (
        <div className="text-[11px] text-slate-400 leading-relaxed">{h.explanation}</div>
      )}
      {confPct !== null && (
        <div className="flex items-center gap-2 pt-1">
          <div className="text-[9px] uppercase tracking-wider text-slate-500">Confidence</div>
          <div className="flex-1 h-1 rounded-full bg-slate-700 overflow-hidden">
            <div className="h-full bg-indigo-400" style={{ width: `${confPct}%` }} />
          </div>
          <div className="text-[10px] font-mono text-slate-400">{confPct}%</div>
        </div>
      )}
    </div>
  );
}

export default function PublicInvestigationPage() {
  const { shareId } = useParams();
  const [state, setState] = useState({ loading: true });
  const [password, setPassword] = useState('');
  const [submittingPw, setSubmittingPw] = useState(false);

  const attempt = useCallback(async (pw) => {
    setState((s) => ({ ...s, loading: true }));
    const r = await fetchShare(shareId, pw);
    setState({ loading: false, ...r });
    setSubmittingPw(false);
  }, [shareId]);

  useEffect(() => { attempt(''); }, [attempt]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-slate-900 text-slate-100 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-indigo-300" />
      </div>
    );
  }

  // Password gate.
  if (state.code === 'password_required' || state.code === 'invalid_password') {
    return (
      <div className="min-h-screen bg-slate-900 text-slate-100 flex items-center justify-center p-4">
        <div className="max-w-sm w-full rounded-lg border border-slate-700 bg-slate-800/60 p-5" data-testid="public-investigation-password-gate">
          <div className="flex items-center gap-2 mb-3">
            <Lock className="w-4 h-4 text-indigo-300" />
            <div className="text-sm font-semibold">Password required</div>
          </div>
          <p className="text-[12px] text-slate-400 mb-3">
            This investigation link is password-protected. Enter the password shared with you.
          </p>
          {state.code === 'invalid_password' && (
            <div className="mb-3 rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-[11px] text-rose-200">
              Incorrect password — try again.
            </div>
          )}
          <form
            onSubmit={(e) => { e.preventDefault(); setSubmittingPw(true); attempt(password); }}
            className="space-y-3"
          >
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              className="w-full bg-slate-900 border border-slate-700 rounded-md px-2.5 py-1.5 text-[12px] outline-none focus:border-indigo-400/60"
              data-testid="public-investigation-password-input"
            />
            <button
              type="submit"
              disabled={submittingPw || !password}
              className="w-full rounded-md bg-indigo-500 px-3 py-2 text-[12px] font-medium text-white hover:bg-indigo-400 disabled:opacity-40"
              data-testid="public-investigation-password-submit"
            >
              {submittingPw ? 'Checking…' : 'Unlock'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Terminal errors — expired / revoked / not found.
  if (!state.ok) {
    const isExpired = state.status === 410;
    const ErrIcon = isExpired ? Clock : ShieldAlert;
    return (
      <div className="min-h-screen bg-slate-900 text-slate-100 flex items-center justify-center p-4">
        <div className="max-w-sm text-center" data-testid="public-investigation-error">
          <ErrIcon className="w-10 h-10 mx-auto mb-3 text-slate-500" />
          <div className="text-sm font-semibold mb-1">
            {isExpired ? 'This share link has expired' : 'Share link unavailable'}
          </div>
          <div className="text-[12px] text-slate-400">
            {state.message || 'Ask the sender for a new link.'}
          </div>
        </div>
      </div>
    );
  }

  const { run, share } = state.data || {};
  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 p-6" data-testid="public-investigation-root">
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="rounded-lg border border-slate-700 bg-slate-800/60 p-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Metric under investigation</div>
          <div className="text-lg font-semibold" data-testid="public-investigation-metric">{run?.metric}</div>
          {run?.note && <div className="mt-2 text-[12px] text-slate-400">Note: {run.note}</div>}
        </div>

        <div className="rounded-lg border border-slate-700 bg-slate-800/60 p-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">Summary</div>
          <div className="text-[13px] leading-relaxed" data-testid="public-investigation-summary">
            {run?.summary || 'No summary provided.'}
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2">
            Hypotheses ({(run?.hypotheses || []).length})
          </div>
          {(!run?.hypotheses || run.hypotheses.length === 0) ? (
            <div className="text-[12px] italic text-slate-400">No hypotheses in this investigation.</div>
          ) : (
            <div className="space-y-2">
              {run.hypotheses.map((h, i) => (
                <Hypothesis key={`${h.dimension}-${h.value}-${i}`} h={h} index={i} />
              ))}
            </div>
          )}
        </div>

        <div className="pt-4 border-t border-slate-800 flex items-center justify-between text-[10px] text-slate-500">
          <div>Shared investigation · {share?.view_count ?? 1} view{(share?.view_count ?? 1) === 1 ? '' : 's'}</div>
          {share?.expires_at && (
            <div>Expires {new Date(share.expires_at).toLocaleDateString()}</div>
          )}
        </div>
      </div>
    </div>
  );
}
